import { getCarrierToken } from '@/auth/useCarrierSession';
import { planRecipients, wantsEmail, wantsSms } from '@/lib/recipientPlan';
import React, { useEffect, useState, useCallback } from 'react';
import CarrierChatModal from './CarrierChatModal';
import GeneralChatButton from './GeneralChatButton';
import { PortalJourneyRow } from './PortalJourneyRow';
import type { JourneyDto } from '@/api/client';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CarrierContact {
    CarrierContactId: number;
    CarrierCode: string;
    Email: string;
    DisplayName: string | null;
    IsActive: boolean;
    /** ⭐ F212: 'Client' on the client portal (carrier/me answers both) */
    Kind?: 'Carrier' | 'Client';
    ClientCode?: string | null;
    ClientName?: string | null;
    CompanyName?: string | null;
}

/** ⭐ F212: one dashboard, two portals. */
type Portal = 'carrier' | 'client';

interface Facility {
    FacilityCode: string;
    Code: string;
    DisplayName: string;
    Timezone: string;
}

interface Appointment {
    AppointmentId: number;
    AppointmentNumber: string;
    FacilityCode: string;
    FacilityName: string;
    DoorCode: string | null;
    StatusCode: string;
    StatusName: string;
    Direction: string;
    ClientCode: string;
    ClientName: string | null;
    DocumentType: string;
    PoNumber: string | null;
    DocumentNumber: string | null;
    ReferenceNumber: string | null;
    PalletCount: number | null;
    ScheduledStartUtc: string;
    ScheduledEndUtc: string;
    ActualArrivalUtc: string | null;
    Comments: string | null;
    CreatedByType: string | null;
    CreatedUtc: string;
    drivers: { appointmentDriverId: number; driverId: number | null; name: string; phone: string }[];
    /** ⭐ F212: the truck's carrier (shown to a client), the portal's Status words and Completed time */
    CarrierCode?: string | null;
    CarrierName?: string | null;
    FacilityTimezone?: string | null;
    ActualDepartureUtc?: string | null;
    PortalStage?: string | null;
    PortalStatus?: string | null;
    CompletedUtc?: string | null;
}

interface Slot {
    startUtc: string;
    endUtc: string;
    startLocal: string;
    endLocal: string;
    availableDoorCount: number;
}

// ─── Step types for the request form ────────────────────────────────────────
type Step = 'facility' | 'date' | 'slot' | 'details' | 'confirm';

interface RequestFormState {
    step: Step;
    facilityCode: string;
    facilityName: string;
    date: string;          // YYYY-MM-DD local
    slot: Slot | null;
    direction: 'INBOUND' | 'OUTBOUND';
    documentType: string;
    documentNumber: string;
    poNumber: string;
    referenceNumber: string;
    palletCount: string;
    comments: string;
    /** ⭐ F212: the carrier a CLIENT picks (a carrier books as itself) */
    carrierCode: string;
}

const INITIAL_FORM: RequestFormState = {
    step: 'facility',
    facilityCode: "",
    facilityName: '',
    date: '',
    slot: null,
    direction: 'INBOUND',
    documentType: 'BOL',
    documentNumber: '',
    poNumber: '',
    referenceNumber: '',
    palletCount: '',
    comments: '',
    carrierCode: '',
};

import {
    useStatusPalette, statusHex, statusName, lightChipStyle,
} from '@/lib/statusPalette';

// ─── Status badge ────────────────────────────────────────────────────────────

/**
 * ---- ⛔⛔ ~~`STATUS_STYLES`~~ — DELETED 2026-09-25 -----------------------
 *
 * > Pete: *"Approved is the wrong color. Needs to match our status colors in
 * > dock scheduler. Same on carrier screen, all the status need to match
 * > across the board."*
 *
 * ⚠️ IT WAS NOT A SHADE. **APPROVED AND ARRIVED WERE EXACTLY SWAPPED.** This
 * map said `APPROVED: bg-green-100` and `ARRIVED: bg-blue-100`; dock.StatusColor
 * has said APPROVED `#1d4ed8` blue and ARRIVED `#047857` green since the
 * 2026-08-12 change that migration 135 records. A carrier has been reading an
 * approved load as green - the colour this dock uses for "it is here" - for six
 * weeks.
 *
 * ⛔ THE OTHER FOUR WERE RIGHT, WHICH IS WHY NOBODY CAUGHT IT. Requested,
 * departed, no-show and cancelled all matched. A palette that is two-thirds
 * correct reads as correct.
 *   ** A COPY IS NOT CHECKED BY THE PARTS OF IT THAT STILL AGREE. **
 *
 * ⭐ The hue now comes from the database through `useStatusPalette`. The LOOK
 * is unchanged - a pale chip with its own hue as the text - because that is
 * what `bg-x-100 / text-x-800` was, rebuilt from one hex.
 */
function StatusBadge({ code, name }: { code: string; name: string }) {
    const palette = useStatusPalette();
    return (
        <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={lightChipStyle(statusHex(palette, code))}
        >
            {statusName(palette, code, name)}
        </span>
    );
}

// ─── Utility ─────────────────────────────────────────────────────────────────









function formatDateOnly(utcStr: string): string {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    }).format(new Date(utcStr));
}

/** ⭐ F212: the Completed column — the facility's own clock, like the journey */
function formatStamp(utcStr: string, tz: string | null | undefined): string {
    const o: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    try { return new Date(utcStr).toLocaleString('en-US', tz ? { ...o, timeZone: tz } : o); }
    catch { return new Date(utcStr).toLocaleString('en-US', o); }
}

/** ⭐ F212: "when it's complete" — the truck has left (the same rule as portal.ts) */
const isComplete = (a: Appointment) => !!a.CompletedUtc || !!a.ActualDepartureUtc || a.StatusCode === 'DEPARTED';
/** a load on site or being worked: its journey row re-reads */
const isLive = (a: Appointment) => ['arrived', 'checkedIn', 'atDoor', 'working', 'worked'].includes(a.PortalStage ?? '');

// ─── API helpers ─────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const token = getCarrierToken();
    const authHeader: Record<string, string> = token ? { 'X-Carrier-Session': token } : {};
    const res = await fetch(`/api/${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...authHeader,
            ...(options?.headers ?? {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    if (res.status === 204) return undefined as unknown as T;
    return res.json();
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CarrierDashboard({ portal = 'carrier' }: { portal?: Portal } = {}) {
    const isClient = portal === 'client';
    const [contact, setContact]       = useState<CarrierContact | null>(null);
    const [appointments, setAppts]    = useState<Appointment[]>([]);
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loading, setLoading]       = useState(true);   // true during ANY load, incl. background refreshes
    /**
     * Whether the FIRST load has finished. The full-page "Loading…" state below
     * is gated on this, not on `loading`.
     *
     * `loading` goes true again on every background refresh, and the early
     * return it used to guard replaced the whole dashboard — including any open
     * modal — with a spinner. So adding a driver unmounted the very dialog you
     * were working in, destroyed its local state, and remounted it from a stale
     * snapshot: the driver was saved, but the dialog came back looking empty.
     */
    const [hasLoaded, setHasLoaded]   = useState(false);
    const [error, setError]           = useState<string | null>(null);
    const [showForm, setShowForm]     = useState(false);
    const [cancellingId, setCancellingId] = useState<number | null>(null);
    const [pendingCancel, setPendingCancel] = useState<Appointment | null>(null);
    const [filterStatus, setFilterStatus] = useState<string>('active');
    /* ⭐ THE TILE HUES COME FROM dock.StatusColor (2026-09-25). One shared
       cached fetch - StatusBadge calls the same hook and they cost one
       request between them. */
    const palette = useStatusPalette();
    const [chatScope, setChatScope] = useState<{ facilityCode: string; facilityName: string; scope: { type: 'APPOINTMENT'; appointmentId: number; label: string } | { type: 'GENERAL'; label: string } } | null>(null);
    // Appointment whose facility directions the carrier wants to send (driver-facing).
    const [directionsAppt, setDirectionsAppt] = useState<Appointment | null>(null);
    // Appointment whose driver the carrier is adding/editing.
    // Held by ID, not as an object: after a refresh the modal must show the
    // appointment as it now IS, not as it was when the button was clicked.
    // String because AppointmentId is BIGINT and arrives as a string.
    const [driverApptId, setDriverApptId] = useState<string | null>(null);
    /** ⭐ F212: the Status words the journey refines (an inbound's WMS receipt), by appointment */
    const [refined, setRefined] = useState<Record<number, string | null>>({});
    /* ⭐ F223 — Pete: *"this is too busy. Can you put the chevron back in and make it so you can
       expand to see the journey?"* Journeys are folded by default; each opens on its own chevron
       (and loads only then). */
    const [openJourneys, setOpenJourneys] = useState<Set<number>>(() => new Set());
    const toggleJourney = (id: number) => setOpenJourneys((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const [openingPdfId, setOpeningPdfId] = useState<number | null>(null);

    // Transient toast (success/error). Auto-dismisses after 4s; also dismissable.
    const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);
    const showToast = useCallback((kind: 'success' | 'error', text: string) => {
        setToast({ kind, text });
    }, []);

    // Load on mount
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [me, appts, facs] = await Promise.all([
                apiFetch<CarrierContact>('carrier/me'),
                apiFetch<Appointment[]>('carrier/appointments'),
                apiFetch<Facility[]>('carrier/facilities'),
            ]);
            // ⛔ F212: a session that belongs to the other portal is sent there, never shown here
            if ((me.Kind === 'Client') !== isClient) {
                window.location.replace(me.Kind === 'Client' ? '/client/dashboard' : '/carrier/dashboard');
                return;
            }
            setContact(me);
            setAppts(appts);
            setFacilities(facs);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load data.');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, [isClient]);

    useEffect(() => { loadData(); }, [loadData]);

    // ⭐ F212: the journey row's read and the Status refinement it reports
    const loadJourney = useCallback(
        (id: number) => apiFetch<JourneyDto>(`portal/appointments/${id}/journey`), []);
    const onRefined = useCallback((id: number, word: string | null) => {
        setRefined((r) => (r[id] === word ? r : { ...r, [id]: word }));
    }, []);

    /**
     * ⭐ F212 — ATTACHMENTS: every document on the load in ONE PDF, opened in a new tab.
     * > Pete: *"Attachments should just open in a .pdf editor or tab and have all the documents
     * > in there at once."*
     * ⚠️ The tab is opened BEFORE the read (a pop-up blocker allows a window opened by the click,
     * not one opened after an await), then pointed at the PDF. The session travels as a header,
     * so the file is fetched here and handed to the tab as a blob — never a link with a token in it.
     */
    const openAttachments = async (appt: Appointment) => {
        const win = window.open('', '_blank');
        setOpeningPdfId(appt.AppointmentId);
        try {
            const token = getCarrierToken();
            const res = await fetch(`/api/portal/appointments/${appt.AppointmentId}/attachments`, {
                headers: token ? { 'X-Carrier-Session': token } : {},
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: res.statusText }));
                throw new Error(err.error ?? `HTTP ${res.status}`);
            }
            const url = URL.createObjectURL(await res.blob());
            if (win) win.location.href = url; else window.location.assign(url);
            setTimeout(() => URL.revokeObjectURL(url), 10 * 60_000);
        } catch (e: unknown) {
            if (win) win.close();
            showToast('error', e instanceof Error ? e.message : 'The attachments could not be opened.');
        } finally {
            setOpeningPdfId(null);
        }
    };

    // Cancel an appointment (confirmation handled by the in-app modal below).
    const runCancel = async (appt: Appointment) => {
        setCancellingId(appt.AppointmentId);
        try {
            await apiFetch(`carrier/appointments/${appt.AppointmentId}`, { method: 'DELETE' });
            await loadData();
            showToast('success', `Appointment ${appt.AppointmentNumber} cancelled.`);
        } catch (e: unknown) {
            showToast('error', e instanceof Error ? e.message : 'Cancel failed.');
        } finally {
            setCancellingId(null);
        }
    };

    // Filtered appointments
    const visibleAppts = appointments.filter(a => {
        if (filterStatus === 'active')   return ['REQUESTED','APPROVED','ARRIVED'].includes(a.StatusCode);
        if (filterStatus === 'pending')  return a.StatusCode === 'REQUESTED';
        if (filterStatus === 'confirmed') return a.StatusCode === 'APPROVED';
        /* ⭐ ADDED 2026-09-25 WITH THE ARRIVED TILE. ⛔ THE TILE COULD NOT SHIP
           WITHOUT IT: the fallthrough below is `return true`, so an unrecognised
           key shows EVERY appointment rather than none. The tile would have
           looked wired and quietly filtered nothing.
             ** A FILTER WHOSE UNKNOWN CASE RETURNS EVERYTHING FAILS BY SHOWING
                TOO MUCH, WHICH IS THE FAILURE NOBODY REPORTS. ** */
        if (filterStatus === 'arrived')  return a.StatusCode === 'ARRIVED';
        if (filterStatus === 'past')     return ['DEPARTED','NOSHOW'].includes(a.StatusCode);
        if (filterStatus === 'cancelled') return a.StatusCode === 'CANCELLED';
        return true;
    });

    // Resolved from the CURRENT list every render, so an add/remove is reflected
    // as soon as loadData() returns.
    const driverAppt = driverApptId === null
        ? null
        : appointments.find(a => String(a.AppointmentId) === driverApptId) ?? null;

    // ⭐ F212: each portal cancels the requests IT made (the server checks the same)
    // ⭐ F223 (popup "Any of its own, if not arrived"): a client cancels any appointment for its
    //   freight while Requested / Approved, whoever booked it; a carrier, only what it requested.
    const canCancel = (a: Appointment) =>
        (isClient || a.CreatedByType === 'Carrier') && ['REQUESTED','APPROVED'].includes(a.StatusCode);

    /* ⭐ F223: a journey to show (not for a cancelled or no-show load) */
    const hasJourney = (a: Appointment) => !['CANCELLED', 'NOSHOW'].includes(a.StatusCode);
    /* ⭐ F223: the Status column's second line, only when it is not the chip's own word */
    const statusLine = (a: Appointment): string | null => {
        const w = refined[a.AppointmentId] || a.PortalStatus;
        if (!w) return null;
        return w.trim().toLowerCase() === (a.StatusName ?? '').trim().toLowerCase() ? null : w;
    };

    // Driver can be added/edited on any of the carrier's own appointments
    // (not just ones they self-requested) as long as it's not terminal --
    // matches the backend lock (DEPARTED/CANCELLED/NOSHOW) in carrierDrivers.ts.
    const canEditDriver = (a: Appointment) =>
        ['REQUESTED','APPROVED'].includes(a.StatusCode);

    // Only before there is anything to show. A refresh keeps the page (and any
    // open dialog) mounted — see the note on hasLoaded.
    if (!hasLoaded) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <div className="text-gray-500 text-sm animate-pulse">Loading your appointments…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="rounded-md bg-red-50 border border-red-200 p-4 text-red-700 text-sm">{error}</div>
                <button onClick={loadData} className="mt-3 text-sm text-blue-600 hover:underline">Try again</button>
            </div>
        );
    }

    return (
        /* FULL WIDTH, deliberately uncapped — same change made to the scheduler
           board, for the same reason (2026-08-17). This was max-w-5xl mx-auto,
           which on a wide monitor threw away half the screen and pushed the
           appointment table into a horizontal scroll: a carrier had to scroll
           sideways to reach Chat / Directions / Drivers / Cancel, the four
           things this page exists for. The padding scales with the viewport so
           a phone still gets comfortable gutters and a 2560px monitor doesn't
           get a column of text down the middle.

           ⚠️ THE PADDING MUST MATCH .app-hdr-inner.is-wide EXACTLY
           (px-4 sm:px-6 2xl:px-8). It was lg:px-8 here for an hour, which put
           the brand 8px out of line with the table beneath it between the lg
           and 2xl breakpoints — the kind of misalignment that only appears at
           one window size and reads as sloppiness rather than a bug. */
        <div className="w-full px-4 sm:px-6 2xl:px-8 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                        {isClient
                            ? (contact?.ClientName || contact?.ClientCode || 'Client Portal')
                            : contact ? (contact.DisplayName ?? contact.CarrierCode) : 'Carrier Portal'}
                    </h1>
                    {contact && (
                        <p className="text-sm text-gray-500 mt-0.5">
                            {isClient
                                ? <>{contact.ClientCode}{contact.CompanyName ? ` · ${contact.CompanyName}` : ''} · {contact.DisplayName ? `${contact.DisplayName} · ` : ''}{contact.Email}</>
                                : <>{contact.CarrierCode} · {contact.Email}</>}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {/* ⭐ F223 (migration 246): chat for both portals — a client's threads are its own */}
                    {<GeneralChatButton
                        facilities={facilities}
                        onPick={(facilityCode, facilityName) => setChatScope({
                            facilityCode,
                            facilityName,
                            scope: { type: 'GENERAL', label: `Chat — ${facilityName}` },
                        })}
                    />}
                <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                    <span className="text-lg leading-none">+</span> Request Appointment
                </button>
                </div>
            </div>

            {/* Summary counts */}
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-3">
                {/**
                  * ---- ⭐ THE TILES SPEAK THE STATUS VOCABULARY (Pete, 2026-09-25) ----
                  *
                  * > *"The items at the top should match the status (pending is
                  * > requested, Confirmed approved."*
                  *
                  * ⛔⛔ AND `Upcoming` BECAME `Arrived`, WHICH IS NOT A RENAME.
                  * Upcoming was a TIME WINDOW (requested + approved + arrived) sitting
                  * in a row of STATUSES, so two tiles answered "what state is it in"
                  * and the third answered "when is it". It also double-counted every
                  * load the first two tiles already showed.
                  *   ** A ROW OF COUNTS THAT DO NOT PARTITION ANYTHING IS THREE
                  *      ANSWERS TO THREE DIFFERENT QUESTIONS. **
                  * The three now partition cleanly: requested, approved, arrived.
                  *
                  * ⚠️ THE LABELS ARE LOCAL, NOT `displayName`. dock.StatusColor calls
                  * APPROVED "Approved / expected", which is right on an admin screen
                  * and too long for a tile a carrier glances at.
                  *   ** ONE SOURCE FOR THE COLOUR DOES NOT MEAN ONE SOURCE FOR EVERY
                  *      WORD THAT DESCRIBES IT. **
                  */}
                {[
                    { label: 'Requested', filterKey: 'pending',   statuses: ['REQUESTED'], code: 'REQUESTED' },
                    { label: 'Approved',  filterKey: 'confirmed', statuses: ['APPROVED'],  code: 'APPROVED'  },
                    { label: 'Arrived',   filterKey: 'arrived',   statuses: ['ARRIVED'],   code: 'ARRIVED'   },
                ].map(({ label, filterKey, statuses, code }) => {
                    const hex = statusHex(palette, code);
                    const on  = filterStatus === filterKey;
                    return (
                    <button
                        key={label}
                        type="button"
                        onClick={() => setFilterStatus(filterKey)}
                        className="text-left rounded-lg border p-3 transition-all hover:shadow-sm"
                        style={{
                            ...lightChipStyle(hex),
                            borderRadius: '0.5rem',
                            /* ⚠️ THE SELECTED RING IS THE SAME HUE, DRAWN AS A SHADOW.
                               Tailwind's ring-* classes cannot take a runtime colour, and
                               a box-shadow ring is what they compile to anyway. */
                            boxShadow: on ? `0 0 0 2px white, 0 0 0 4px ${hex}` : undefined,
                        }}
                    >
                        <div className="text-2xl font-bold">
                            {appointments.filter(a => statuses.includes(a.StatusCode)).length}
                        </div>
                        <div className="text-xs font-medium mt-0.5">{label}</div>
                    </button>
                    );
                })}
            </div>

            {/* Filter tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex gap-6">
                    {[
                        { key: 'active',    label: 'Active' },
                        { key: 'past',      label: 'Completed' },
                        { key: 'cancelled', label: 'Cancelled' },
                        { key: 'all',       label: 'All' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFilterStatus(tab.key)}
                            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                                filterStatus === tab.key
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Appointments table */}
            {visibleAppts.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                    <div className="text-4xl mb-2">📋</div>
                    <div className="text-sm">No appointments in this view.</div>
                    {filterStatus === 'active' && (
                        <button
                            onClick={() => setShowForm(true)}
                            className="mt-3 text-sm text-blue-600 hover:underline"
                        >
                            Request your first appointment →
                        </button>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
                    {/* ⭐ F219 — Pete: *"Need a little more separtion color on each apppointment. Mabye
                        appoit # is bold and the outline of the appointment is darker and mouse over
                        occurs on the appointments"*. Each appointment is its OWN <tbody> (its row and its
                        journey row together): a darker rule between appointments, and hovering anywhere
                        on it shades both rows and draws a blue bar down its left edge. */}
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 border-b-2 border-gray-300">
                            <tr>
                                {/* ⭐ F212: Status shows the load's own words beside the status chip; Completed is
                                    when the truck was inbounded / outbounded; a client also sees the carrier */}
                                {['Appointment #', 'Status', 'Facility', 'Scheduled', 'Direction', ...(isClient ? ['Carrier'] : []), 'PO / Doc', 'Pallets', 'Completed', ''].map(h => (
                                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                            {visibleAppts.map(appt => (
                                <tbody key={appt.AppointmentId} className="group portal-appt bg-white border-b-2 border-gray-300 last:border-b-0">
                                <tr
                                    className={`transition-colors group-hover:bg-blue-50/60 ${
                                        appt.StatusCode === 'CANCELLED' ? 'opacity-60' : ''
                                    }`}
                                >
                                    <td className="px-3 py-3 font-mono text-sm font-bold text-gray-900 whitespace-nowrap border-l-4 border-transparent group-hover:border-blue-500 transition-colors">
                                        {hasJourney(appt) ? (
                                            <button
                                                type="button"
                                                onClick={() => toggleJourney(appt.AppointmentId)}
                                                aria-expanded={openJourneys.has(appt.AppointmentId)}
                                                title={openJourneys.has(appt.AppointmentId) ? 'Hide the journey' : 'Show the journey'}
                                                className="inline-flex items-center gap-1.5 hover:text-blue-700"
                                            >
                                                <span aria-hidden="true" className={`inline-block text-gray-500 transition-transform ${openJourneys.has(appt.AppointmentId) ? 'rotate-90' : ''}`}>▸</span>
                                                {appt.AppointmentNumber}
                                            </button>
                                        ) : (
                                            <span className="pl-[18px]">{appt.AppointmentNumber}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <StatusBadge code={appt.StatusCode} name={appt.StatusName} />
                                        {appt.CreatedByType === (isClient ? 'Client' : 'Carrier') && (
                                            <span className="ml-1 text-[10px] text-blue-500 font-medium">self-req</span>
                                        )}
                                        {/* ⭐ F223: the second line only when it says more than the chip (Pete: "the status is listed twice") */}
                                        {statusLine(appt) && (
                                            <div className={`mt-0.5 text-xs font-medium ${appt.PortalStage === 'working' ? 'text-amber-700' : 'text-gray-600'}`}>
                                                {statusLine(appt)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-3 text-gray-700 whitespace-nowrap">{appt.FacilityName}</td>
                                    <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                                        <div>{formatDateOnly(appt.ScheduledStartUtc)}</div>
                                        <div className="text-gray-400 text-xs">
                                            {new Date(appt.ScheduledStartUtc).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                            {' – '}
                                            {new Date(appt.ScheduledEndUtc).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <span className={`text-xs font-medium ${appt.Direction === 'INBOUND' ? 'text-indigo-600' : 'text-orange-600'}`}>
                                            {appt.Direction === 'INBOUND' ? '↓ In' : '↑ Out'}
                                        </span>
                                    </td>
                                    {isClient && (
                                        <td className="px-3 py-3 text-gray-700 whitespace-nowrap">{appt.CarrierName || appt.CarrierCode || '—'}</td>
                                    )}
                                    <td className="px-3 py-3 text-gray-700">
                                        {appt.PoNumber && <div className="text-xs">PO: {appt.PoNumber}</div>}
                                        {appt.DocumentNumber && <div className="text-xs text-gray-400">{appt.DocumentType}: {appt.DocumentNumber}</div>}
                                    </td>
                                    <td className="px-3 py-3 text-gray-700 text-sm whitespace-nowrap text-tabular">
                                        {appt.PalletCount != null ? appt.PalletCount : '—'}
                                    </td>
                                    <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">
                                        {appt.CompletedUtc ? formatStamp(appt.CompletedUtc, appt.FacilityTimezone) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="pl-3 pr-4 py-3 text-left whitespace-nowrap">
                                        <div className="flex items-center justify-start gap-1">
                                            {/* ⭐ F212: the attachments, one PDF, once the load is complete */}
                                            <button
                                                onClick={() => void openAttachments(appt)}
                                                disabled={!isComplete(appt) || openingPdfId === appt.AppointmentId}
                                                title={!isComplete(appt) ? 'Available once the load is complete' : 'Open every document on this load as one PDF'}
                                                className="inline-flex items-center justify-center px-2.5 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-700 disabled:hover:border-gray-200"
                                            >
                                                {openingPdfId === appt.AppointmentId ? 'Opening…' : 'Attachments'}
                                            </button>
                                            {/* ⭐ F223: Chat and Directions on both portals; Driver is the carrier's only */}
                                            <button
                                                onClick={() => setChatScope({
                                                    facilityCode: appt.FacilityCode,
                                                    facilityName: appt.FacilityName,
                                                    scope: { type: 'APPOINTMENT', appointmentId: appt.AppointmentId, label: `Chat — ${appt.AppointmentNumber}` },
                                                })}
                                                className="inline-flex items-center justify-center px-2.5 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 transition-colors"
                                            >
                                                Chat
                                            </button>
                                            <button
                                                onClick={() => setDirectionsAppt(appt)}
                                                className="inline-flex items-center justify-center px-2.5 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 transition-colors"
                                            >
                                                Directions
                                            </button>
                                            {!isClient && (
                                            <button
                                                onClick={() => setDriverApptId(String(appt.AppointmentId))}
                                                disabled={!canEditDriver(appt)}
                                                title={!canEditDriver(appt) ? `Locked — appointment is ${appt.StatusName.toLowerCase()}` : undefined}
                                                className="inline-flex items-center justify-center min-w-[76px] px-2.5 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-700 disabled:hover:border-gray-200"
                                            >
                                                {appt.drivers.length > 0 ? `Drivers (${appt.drivers.length})` : 'Driver'}
                                            </button>
                                            )}
                                            <button
                                                onClick={() => setPendingCancel(appt)}
                                                disabled={!canCancel(appt) || cancellingId === appt.AppointmentId}
                                                title={!canCancel(appt) ? (['REQUESTED','APPROVED'].includes(appt.StatusCode) ? 'Booked by someone else — contact the scheduler to cancel' : `Can't cancel once ${appt.StatusName.toLowerCase()}`) : undefined}
                                                className="inline-flex items-center justify-center px-2.5 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-700 disabled:hover:border-gray-200"
                                            >
                                                {cancellingId === appt.AppointmentId ? 'Cancelling…' : 'Cancel'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {/* ⭐ F212: the journey, on the row below (not for a cancelled or no-show load) */}
                                {hasJourney(appt) && openJourneys.has(appt.AppointmentId) && (
                                    <tr className="bg-gray-100 border-t border-gray-200 transition-colors group-hover:bg-blue-50/60">
                                        <td colSpan={isClient ? 10 : 9} className="px-3 pt-2 pb-3 border-l-4 border-transparent group-hover:border-blue-500 transition-colors">
                                            <PortalJourneyRow
                                                appointmentId={appt.AppointmentId}
                                                live={isLive(appt)}
                                                load={loadJourney}
                                                onStatus={onRefined}
                                            />
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            ))}
                    </table>
                </div>
            )}

            {/* Request form modal */}
            {showForm && (
                <RequestAppointmentModal
                    portal={portal}
                    facilities={facilities}
                    onClose={() => setShowForm(false)}
                    onSubmitted={async () => {
                        setShowForm(false);
                        await loadData();
                        showToast('success', 'Appointment request submitted. A scheduler will review and confirm it.');
                    }}
                />
            )}

            {chatScope && contact && (
                <CarrierChatModal
                    apiFetch={apiFetch}
                    facilityCode={chatScope.facilityCode}
                    facilityName={chatScope.facilityName}
                    carrierDisplayName={contact.DisplayName ?? (isClient ? (contact.ClientCode ?? contact.Email) : contact.CarrierCode)}
                    scope={chatScope.scope}
                    onClose={() => setChatScope(null)}
                />
            )}

            {directionsAppt && (
                <CarrierDirectionsModal
                    appt={directionsAppt}
                    defaultEmail={contact?.Email ?? null}
                    onClose={() => setDirectionsAppt(null)}
                />
            )}

            {/* A background refresh is now visible instead of blanking the page. */}
            {loading && (
                <div className="fixed bottom-4 right-4 z-[90] rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 shadow">
                    Refreshing…
                </div>
            )}

            {driverAppt && (
                <CarrierDriverModal
                    appt={driverAppt}
                    onClose={() => setDriverApptId(null)}
                    onSaved={async () => {
                        // Refresh the underlying list/calendar data after each
                        // add/remove, but do NOT close — this is a
                        // list-management modal now, and the user may add/remove
                        // several drivers before closing it themselves via its
                        // own Close button. The modal keeps its own local driver
                        // list in sync, so it doesn't depend on this refresh.
                        await loadData();
                    }}
                />
            )}

            {/* Toast (top-center, success/error) */}
            {toast && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] max-w-md w-[calc(100%-2rem)] sm:w-auto">
                    <div
                        className={`flex items-start gap-3 rounded-lg shadow-lg border px-4 py-3 text-sm ${
                            toast.kind === 'success'
                                ? 'bg-green-50 border-green-300 text-green-800'
                                : 'bg-red-50 border-red-300 text-red-800'
                        }`}
                    >
                        <span className="flex-1">{toast.text}</span>
                        <button
                            type="button"
                            onClick={() => setToast(null)}
                            aria-label="Dismiss"
                            className={`shrink-0 -mr-1 -mt-0.5 text-lg leading-none font-medium ${
                                toast.kind === 'success'
                                    ? 'text-green-600 hover:text-green-800'
                                    : 'text-red-600 hover:text-red-800'
                            }`}
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
            {/* Cancel confirmation — in-app, matches the portal modal style */}
            {pendingCancel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
                        <div className="px-6 py-5">
                            <h3 className="text-lg font-semibold text-gray-900">Cancel appointment?</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                Cancel appointment {pendingCancel.AppointmentNumber}? This can&apos;t be undone.
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => setPendingCancel(null)}
                                disabled={cancellingId !== null}
                                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Keep appointment
                            </button>
                            <button
                                type="button"
                                onClick={() => { const a = pendingCancel; setPendingCancel(null); void runCancel(a); }}
                                disabled={cancellingId !== null}
                                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            >
                                Cancel appointment
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Multi-step request form ─────────────────────────────────────────────────

function RequestAppointmentModal({
    portal = 'carrier',
    facilities,
    onClose,
    onSubmitted,
}: {
    portal?: Portal;
    facilities: Facility[];
    onClose: () => void;
    onSubmitted: () => void;
}) {
    const isClient = portal === 'client';
    /** ⭐ F212: the carriers a client may book with (its company's), from portal/carriers */
    const [carrierOptions, setCarrierOptions] = useState<{ CarrierCode: string; CarrierName: string }[] | 'loading' | { error: string }>('loading');
    useEffect(() => {
        if (!isClient) return;
        let cancelled = false;
        apiFetch<{ carriers: { CarrierCode: string; CarrierName: string }[] }>('portal/carriers')
            .then((d) => { if (!cancelled) setCarrierOptions(d.carriers); })
            .catch((e) => { if (!cancelled) setCarrierOptions({ error: e instanceof Error ? e.message : 'Failed to load carriers.' }); });
        return () => { cancelled = true; };
    }, [isClient]);
    const [form, setForm]       = useState<RequestFormState>(() => {
        const d = new Date();
        const ymd = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        return { ...INITIAL_FORM, date: ymd };
    });
    const [slots, setSlots]     = useState<Slot[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [slotsError, setSlotsError]     = useState<string | null>(null);
    const [submitting, setSubmitting]     = useState(false);
    const [submitError, setSubmitError]   = useState<string | null>(null);

    // Drivers to attach at creation (optional). Staged locally; attached
    // best-effort AFTER the appointment is created (need the new
    // AppointmentId first). Roster is the carrier's OWN drivers only
    // (GET carrier/drivers is server-scoped to their carrier).
    const [stagedDrivers, setStagedDrivers] = useState<{ driverId: number | null; name: string; phone: string; email?: string | null }[]>([]);
    const [driverMode, setDriverMode] = useState<'existing' | 'new'>('existing');
    const [driverRoster, setDriverRoster] = useState<CarrierDriverRow[] | 'loading' | { error: string }>('loading');
    const [pendingRosterId, setPendingRosterId] = useState<number | null>(null);
    const [newDriverName, setNewDriverName] = useState('');
    const [newDriverPhone, setNewDriverPhone] = useState('');
    const [newDriverEmail, setNewDriverEmail] = useState('');

    // Load the carrier's own roster once (for pick-from-roster at creation).
    // ⛔ F212: not on the client portal — drivers are the carrier's (arch §1K)
    useEffect(() => {
        if (isClient) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await apiFetch<{ drivers: CarrierDriverRow[] }>('carrier/drivers');
                if (!cancelled) setDriverRoster(data.drivers);
            } catch (e) {
                if (!cancelled) setDriverRoster({ error: e instanceof Error ? e.message : 'Failed to load roster.' });
            }
        })();
        return () => { cancelled = true; };
    }, [isClient]);

    /**
     * The drivers that will actually be attached.
     *
     * A carrier who picks a driver from the dropdown, or types a new one, and
     * then hits "Review" without clicking Add used to lose that driver in
     * silence: the appointment was created with none, and nothing said so. This
     * folds any pending selection into the list, and it is computed on the spot
     * rather than read from state, so it cannot be stale at submit time.
     */
    const effectiveDrivers = (): typeof stagedDrivers => {
        const out = [...stagedDrivers];
        if (driverMode === 'existing' && pendingRosterId != null) {
            const list = Array.isArray(driverRoster) ? driverRoster : [];
            const picked = list.find(d => d.driverId === pendingRosterId);
            if (picked && !out.some(d => d.driverId === picked.driverId)) {
                out.push({ driverId: picked.driverId, name: picked.name, phone: picked.phone });
            }
        }
        if (driverMode === 'new' && newDriverName.trim() && newDriverPhone.trim()) {
            const name = newDriverName.trim();
            const phone = newDriverPhone.trim();
            if (!out.some(d => d.driverId == null && d.name === name && d.phone === phone)) {
                out.push({ driverId: null, name, phone, email: newDriverEmail.trim() || null });
            }
        }
        return out;
    };

    /** Picking from the roster stages immediately — there is no Add step. */
    const stageRosterDriver = (driverId: number | null) => {
        setSubmitError(null);
        if (driverId == null) return;
        if (stagedDrivers.some(d => d.driverId === driverId)) return;   // not offered, but be safe
        const list = Array.isArray(driverRoster) ? driverRoster : [];
        const picked = list.find(d => d.driverId === driverId);
        if (!picked) { setSubmitError('Selected driver not found.'); return; }
        setStagedDrivers(prev => [...prev, { driverId: picked.driverId, name: picked.name, phone: picked.phone }]);
        setPendingRosterId(null);
    };

    const stageAddDriver = () => {
        setSubmitError(null);
        if (driverMode === 'existing') {
            stageRosterDriver(pendingRosterId);
        } else {
            if (!newDriverName.trim() || !newDriverPhone.trim()) { setSubmitError('New driver needs a name and phone.'); return; }
            setStagedDrivers(prev => [...prev, { driverId: null, name: newDriverName.trim(), phone: newDriverPhone.trim(), email: newDriverEmail.trim() || null }]);
            setNewDriverName(''); setNewDriverPhone(''); setNewDriverEmail('');
        }
    };
    const stageRemoveDriver = (index: number) => setStagedDrivers(prev => prev.filter((_, i) => i !== index));

    const patch = (updates: Partial<RequestFormState>) =>
        setForm(f => ({ ...f, ...updates }));

    // True when the current submitError is specifically a PO-resolution failure,
    // so we can highlight the PO field and show the message beside it.
    const poError = !!submitError && (/^PO not found/i.test(submitError) || /^That PO matches/i.test(submitError));

    // Step labels
    const STEPS: Step[] = ['facility', 'date', 'slot', 'details', 'confirm'];
    const stepIndex = STEPS.indexOf(form.step);

    const goBack = () => {
        if (stepIndex > 0) patch({ step: STEPS[stepIndex - 1] });
    };

    // Load slots when date is chosen
    const loadSlots = async (facilityCode: string, date: string, direction: 'INBOUND' | 'OUTBOUND' = 'INBOUND') => {
        setLoadingSlots(true);
        setSlotsError(null);
        setSlots([]);
        try {
            const data = await apiFetch<{
                slots: Slot[];
                holiday?: { name: string };
                closed?: boolean;
            }>(
                `carrier/available-slots?facilityCode=${facilityCode}&date=${date}&direction=${direction}`
            );
            setSlots(data.slots);
            if (data.holiday) {
                setSlotsError(`This date is a company holiday (${data.holiday.name}). No appointments can be scheduled.`);
            } else if (data.closed) {
                setSlotsError('This facility is closed on that day. Please choose a different date.');
            } else if (data.slots.length === 0) {
                setSlotsError('No available slots on this date — all doors are fully booked. Try a different day.');
            }
        } catch (e: unknown) {
            setSlotsError(e instanceof Error ? e.message : 'Could not load slots.');
        } finally {
            setLoadingSlots(false);
        }
    };

    // Submit
    const handleSubmit = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            const created = await apiFetch<{ AppointmentId: number }>('carrier/appointments', {
                method: 'POST',
                body: JSON.stringify({
                    facilityCode:        form.facilityCode,
                    scheduledStartUtc: form.slot!.startUtc,
                    scheduledEndUtc:   form.slot!.endUtc,
                    direction:         form.direction,
                    documentType:      form.documentType,
                    documentNumber:    form.documentNumber || null,
                    poNumber:          form.poNumber || null,
                    referenceNumber:   form.referenceNumber || null,
                    palletCount:       Number(form.palletCount),
                    comments:          form.comments || null,
                    ...(isClient ? { carrierCode: form.carrierCode || null } : {}),
                }),
            });

            // Attach staged drivers best-effort. The appointment already exists,
            // so a driver-add failure must NOT discard it — collect failures and
            // warn instead. Endpoint is server-scoped to this carrier.
            const driverFailures: string[] = [];
            for (const d of isClient ? [] : effectiveDrivers()) {
                try {
                    const body = d.driverId != null
                        ? { driverId: d.driverId }
                        : { name: d.name, phone: d.phone, email: d.email ?? null };
                    await apiFetch(`carrier/appointments/${created.AppointmentId}/drivers`, {
                        method: 'POST',
                        body: JSON.stringify(body),
                    });
                } catch {
                    driverFailures.push(d.name);
                }
            }
            if (driverFailures.length > 0) {
                // Created, but some drivers didn't attach. Refresh the list so
                // the new appointment shows, then warn — they can add the
                // missing drivers from the appointment's Drivers button.
                onSubmitted();
                setSubmitError(`Appointment requested, but couldn't add: ${driverFailures.join(', ')}. Add them from the appointment's Drivers button.`);
                setSubmitting(false);
                return;
            }
            onSubmitted();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Submission failed.';
            setSubmitError(msg);
            setSubmitting(false);
            // PO-resolution failures: send them back to the details step so they
            // can correct the PO right where they typed it.
            if (/^PO not found/i.test(msg) || /^That PO matches/i.test(msg)) {
                patch({ step: 'details' });
            }
        }
    };

    // Today in YYYY-MM-DD for min date input
    const localYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const today = localYmd(new Date());
    // Max date: 90 days out
    const maxDate = localYmd(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Modal header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Request Appointment</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>

                {/* Step indicator */}
                <div className="px-6 pt-4">
                    <div className="flex items-center gap-1">
                        {STEPS.map((s, i) => (
                            <React.Fragment key={s}>
                                <div className={`h-1.5 flex-1 rounded-full transition-colors ${
                                    i <= stepIndex ? 'bg-blue-500' : 'bg-gray-200'
                                }`} />
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="mt-1.5 text-xs text-gray-400">
                        Step {stepIndex + 1} of {STEPS.length}
                    </div>
                </div>

                <div className="px-6 py-5 space-y-4">

                    {/* ── Step 1: Facility ── */}
                    {form.step === 'facility' && (
                        <div className="space-y-3">
                            <h3 className="font-medium text-gray-800">Select a facility</h3>
                            <div className="grid grid-cols-1 gap-2">
                                {facilities.map(f => (
                                    <button
                                        key={f.FacilityCode}
                                        onClick={() => {
                                            patch({ facilityCode: f.FacilityCode, facilityName: f.DisplayName, step: 'date' });
                                        }}
                                        className="text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                                    >
                                        <div className="font-medium text-gray-800">{f.DisplayName}</div>
                                        <div className="text-xs text-gray-400">{f.Code} · {f.Timezone}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Step 2: Date + Direction ── */}
                    {form.step === 'date' && (
                        <div className="space-y-3">
                            <h3 className="font-medium text-gray-800">
                                Choose a date — <span className="text-gray-500 font-normal">{form.facilityName}</span>
                            </h3>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {(['INBOUND', 'OUTBOUND'] as const).map(d => (
                                        <button
                                            key={d}
                                            onClick={() => patch({ direction: d })}
                                            className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                form.direction === d
                                                    ? d === 'INBOUND'
                                                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                        : 'border-orange-500 bg-orange-50 text-orange-700'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                        >
                                            {d === 'INBOUND' ? '↓ Inbound' : '↑ Outbound'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                                <input
                                    type="date"
                                    min={today}
                                    max={maxDate}
                                    value={form.date}
                                    onChange={e => patch({ date: e.target.value })}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>

                            <button
                                disabled={!form.date}
                                onClick={async () => {
                                    patch({ step: 'slot', slot: null });
                                    await loadSlots(form.facilityCode!, form.date, form.direction);
                                }}
                                className="w-full py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors"
                            >
                                See Available Slots →
                            </button>
                        </div>
                    )}

                    {/* ── Step 3: Slot picker ── */}
                    {form.step === 'slot' && (
                        <div className="space-y-3">
                            <h3 className="font-medium text-gray-800">
                                Pick a time slot — <span className="text-gray-500 font-normal">{formatDateOnly(form.date + 'T12:00:00Z')}</span>
                            </h3>
                            {loadingSlots && (
                                <div className="text-sm text-gray-400 animate-pulse">Loading available slots…</div>
                            )}
                            {slotsError && (
                                <div className="text-sm text-red-500">{slotsError}</div>
                            )}
                            {!loadingSlots && slots.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">
                                    {slots.map(slot => (
                                        <button
                                            key={slot.startUtc}
                                            onClick={() => patch({ slot, step: 'details' })}
                                            className={`py-2.5 px-2 rounded-lg border text-sm font-medium transition-colors ${
                                                form.slot?.startUtc === slot.startUtc
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                                            }`}
                                        >
                                            {slot.startLocal}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 4: Details ── */}
                    {form.step === 'details' && (
                        <div className="space-y-3">
                            <h3 className="font-medium text-gray-800">Appointment details</h3>
                            <p className="text-xs text-gray-500 -mt-1"><span className="text-red-500">*</span> required fields</p>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                                    <select
                                        value={form.direction}
                                        onChange={e => patch({ direction: e.target.value as 'INBOUND' | 'OUTBOUND' })}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    >
                                        <option value="INBOUND">Inbound</option>
                                        <option value="OUTBOUND">Outbound</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Doc Type</label>
                                    <select
                                        value={form.documentType}
                                        onChange={e => patch({ documentType: e.target.value })}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    >
                                        {['BOL','PO','ASN','SO','Other'].map(t => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {form.direction === 'INBOUND' && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        PO Number <span className="text-gray-400 font-normal">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.poNumber}
                                        onChange={e => { if (submitError) setSubmitError(null); patch({ poNumber: e.target.value }); }}
                                        placeholder="e.g. PO-123456"
                                        autoFocus={poError}
                                        className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${poError ? 'border-red-400 focus:ring-red-400 bg-red-50' : 'border-gray-300 focus:ring-blue-400'}`}
                                    />
                                    {poError && (
                                        <p className="mt-1 text-xs text-red-600">{submitError}</p>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Document Number
                                </label>
                                <input
                                    type="text"
                                    value={form.documentNumber}
                                    onChange={e => patch({ documentNumber: e.target.value })}
                                    placeholder="BOL / shipment number"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Reference Number</label>
                                <input
                                    type="text"
                                    value={form.referenceNumber}
                                    onChange={e => patch({ referenceNumber: e.target.value })}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Pallet Count <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    step={1}
                                    inputMode="numeric"
                                    value={form.palletCount}
                                    onChange={e => patch({ palletCount: e.target.value })}
                                    placeholder="e.g. 12"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Comments</label>
                                <textarea
                                    value={form.comments}
                                    onChange={e => patch({ comments: e.target.value })}
                                    rows={2}
                                    placeholder="Any special instructions for the scheduler…"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                                />
                            </div>

                            {/* ⭐ F212: a client picks the carrier bringing / collecting the load */}
                            {isClient && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Carrier <span className="text-red-500">*</span>
                                    </label>
                                    {carrierOptions === 'loading' && <span className="text-xs text-gray-400">Loading…</span>}
                                    {!Array.isArray(carrierOptions) && carrierOptions !== 'loading' && (
                                        <span className="text-xs text-red-600">{carrierOptions.error}</span>
                                    )}
                                    {Array.isArray(carrierOptions) && (
                                        <select
                                            value={form.carrierCode}
                                            onChange={e => patch({ carrierCode: e.target.value })}
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        >
                                            <option value="">Select a carrier…</option>
                                            {carrierOptions.map(c => (
                                                <option key={c.CarrierCode} value={c.CarrierCode}>{c.CarrierName || c.CarrierCode}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}

                            {/* Drivers (optional) — pick from your own roster or add
                                a new one. Attached after the request is submitted. */}
                            {!isClient && <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Drivers <span className="text-gray-400 font-normal">(optional)</span></label>
                                {stagedDrivers.length > 0 && (
                                    <div className="border border-gray-200 rounded-md divide-y mb-2">
                                        {stagedDrivers.map((d, i) => (
                                            <div key={`${d.driverId ?? 'new'}-${i}`} className="flex items-center justify-between gap-2 px-3 py-1.5">
                                                <div className="min-w-0">
                                                    <span className="text-sm font-medium text-gray-900">{d.name}</span>
                                                    <span className="text-xs text-gray-500"> · {d.phone}</span>
                                                </div>
                                                <button type="button" onClick={() => stageRemoveDriver(i)} className="text-xs text-red-500 hover:text-red-700 shrink-0">Remove</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-center gap-2 mb-2">
                                    <button type="button" onClick={() => setDriverMode('existing')}
                                        className={`px-3 py-1 rounded-md border text-xs ${driverMode === 'existing' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                        Pick from roster
                                    </button>
                                    <button type="button" onClick={() => setDriverMode('new')}
                                        className={`px-3 py-1 rounded-md border text-xs ${driverMode === 'new' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                        Add new driver
                                    </button>
                                </div>
                                {driverMode === 'existing' && (
                                    <div className="flex items-center gap-2">
                                        {driverRoster === 'loading' && <span className="text-xs text-gray-400">Loading…</span>}
                                        {driverRoster && !Array.isArray(driverRoster) && driverRoster !== 'loading' && (
                                            <span className="text-xs text-red-600">Couldn&apos;t load roster.</span>
                                        )}
                                        {Array.isArray(driverRoster) && (
                                            <>
                                                {/* Stage on SELECT. The Add button that used to sit
                                                    here is gone: choosing a driver already IS the
                                                    decision, and a button that only ever repeats what
                                                    the dropdown just did is one more thing to forget.
                                                    "Add new driver" keeps its confirm, because a
                                                    half-typed name and phone is not a decision. */}
                                                <select
                                                    value={pendingRosterId ?? ''}
                                                    onChange={e => stageRosterDriver(e.target.value ? Number(e.target.value) : null)}
                                                    className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                >
                                                    <option value="">Select a driver…</option>
                                                    {driverRoster
                                                        .filter(d => d.isActive)
                                                        .filter(d => !stagedDrivers.some(sd => sd.driverId === d.driverId))
                                                        .map(d => (
                                                            <option key={d.driverId} value={d.driverId}>{d.name} · {d.phone}</option>
                                                        ))}
                                                </select>
                                            </>
                                        )}
                                    </div>
                                )}
                                {driverMode === 'new' && (
                                    <div className="space-y-2">
                                        <input type="text" value={newDriverName} onChange={e => setNewDriverName(e.target.value)} placeholder="Driver name"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                        <input type="tel" value={newDriverPhone} onChange={e => setNewDriverPhone(e.target.value)} placeholder="+1 555 555 5555"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                        <input type="email" value={newDriverEmail} onChange={e => setNewDriverEmail(e.target.value)} placeholder="Email (optional)"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                        <button type="button" onClick={stageAddDriver} className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">Add driver</button>
                                    </div>
                                )}
                            </div>}

                            <button
                                disabled={
                                    !(Number.isInteger(Number(form.palletCount)) && Number(form.palletCount) >= 1)
                                    || (isClient && !form.carrierCode)
                                }
                                onClick={() => patch({ step: 'confirm' })}
                                className="w-full py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors"
                            >
                                Review →
                            </button>
                        </div>
                    )}

                    {/* ── Step 5: Confirm ── */}
                    {form.step === 'confirm' && (
                        <div className="space-y-4">
                            <h3 className="font-medium text-gray-800">Review & Submit</h3>
                            <div className="rounded-lg bg-gray-50 border border-gray-200 divide-y divide-gray-200 text-sm">
                                {[
                                    ['Facility',    form.facilityName],
                                    ['Date & Time', form.slot ? `${form.slot.startLocal} – ${form.slot.endLocal}` : ''],
                                    ['Direction',   form.direction],
                                    ['Doc Type',    form.documentType],
                                    ['PO Number',   form.poNumber || '—'],
                                    ['Doc Number',  form.documentNumber || '—'],
                                    ['Reference',   form.referenceNumber || '—'],
                                    ['Pallet Count', form.palletCount || '—'],
                                    ...(isClient
                                        ? [['Carrier', (Array.isArray(carrierOptions) ? carrierOptions.find(c => c.CarrierCode === form.carrierCode)?.CarrierName : null) || form.carrierCode || '—']]
                                        : [['Drivers',     effectiveDrivers().length > 0 ? effectiveDrivers().map(d => d.name).join(', ') : '—']]),
                                    ['Comments',    form.comments || '—'],
                                ].map(([label, value]) => (
                                    <div key={label} className="flex justify-between px-4 py-2.5">
                                        <span className="text-gray-500">{label}</span>
                                        <span className="font-medium text-gray-800 text-right max-w-[60%]">{value}</span>
                                    </div>
                                ))}
                            </div>

                            {!submitError && (
                                <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-700">
                                    Your request will land as <strong>Requested</strong> status. A scheduler will review and confirm your time slot. You'll see the status update here.
                                </div>
                            )}

                            {submitError && (
                                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-xs text-red-700">
                                    {submitError}
                                </div>
                            )}

                            <button
                                disabled={submitting}
                                onClick={handleSubmit}
                                className="w-full py-2 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40 transition-colors"
                            >
                                {submitting ? 'Submitting…' : 'Submit Request'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Back / cancel footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                    {stepIndex > 0 ? (
                        <button onClick={goBack} className="text-sm text-gray-500 hover:text-gray-700">
                            ← Back
                        </button>
                    ) : (
                        <span />
                    )}
                    <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}





// ─── Carrier directions modal ─────────────────────────────────────────────────
// Lets a carrier send this appointment's facility directions (address + arrival
// instructions + map link) to themselves and/or their driver, by email and/or
// text. Mirrors the scheduler-side Send Directions UI, but calls the carrier-
// gated endpoint (facility derived server-side from the carrier's OWN
// appointment) and prefills the carrier's logged-in email. Texting is LIVE
// (toll-free verification cleared 2026-08-14) and goes through Pipeline's
// notification engine, which applies the SMS consent gate — a driver who
// replied STOP comes back as opted out, not as a failure.
function CarrierDirectionsModal({
    appt,
    defaultEmail,
    onClose,
}: {
    appt: Appointment;
    defaultEmail: string | null;
    onClose: () => void;
}) {
    const [channel, setChannel]       = useState<'email' | 'sms' | 'both'>('email');
    const [emailInput, setEmailInput] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [emails, setEmails]         = useState<string[]>(defaultEmail ? [defaultEmail] : []);
    const [phones, setPhones]         = useState<string[]>([]);
    const [sending, setSending]       = useState(false);
    const [msg, setMsg]               = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);

    const addEmail = () => {
        const v = emailInput.trim();
        if (v && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) && !emails.includes(v)) {
            setEmails([...emails, v]); setEmailInput('');
        }
    };
    const addPhone = () => {
        const v = phoneInput.trim();
        if (v && !phones.includes(v)) { setPhones([...phones, v]); setPhoneInput(''); }
    };

    /**
     * ⛔ THE RECIPIENT RULES NOW LIVE IN `@/lib/recipientPlan` (extracted
     * 2026-08-27). This page and the scheduler's SendDirectionsDialog each had
     * their own copy, and ⚠️ THEY HAD ALREADY DRIFTED — the refusal checks ran in
     * a different ORDER here, so an empty email-only send said "Add at least one
     * email" on this page and "Add at least one recipient" on the scheduler's.
     * ⭐ The guard that was meant to keep the copies together only compared
     * `collectRecipients`, not the ordering around it, so it never saw the gap.
     */
    const handleSend = async () => {
        setMsg(null);
        const wantEmail = wantsEmail(channel);
        const wantSms   = wantsSms(channel);

        const plan = planRecipients({ channel, emails, phones, emailInput, phoneInput });
        // ⚠️ COMMIT BEFORE REPORTING — a carrier must SEE the number they typed
        // was accepted. `commit` is false only for an unparseable address, where
        // clearing the box would delete the text they need to correct.
        if (plan.commit) {
            setEmails(plan.emails); setPhones(plan.phones);
            if (wantEmail) setEmailInput('');
            if (wantSms) setPhoneInput('');
        }
        if (!plan.ok) { setMsg({ kind: 'err', text: plan.error ?? 'Add at least one recipient.' }); return; }
        const sendEmails = plan.emails;
        const sendPhones = plan.phones;

        setSending(true);
        try {
            const data = await apiFetch<SendResponse>(
                `carrier/appointments/${appt.AppointmentId}/send-directions`,
                { method: 'POST', body: JSON.stringify({ channel, emails: sendEmails, phones: sendPhones }) },
            );
            // Same split as the scheduler's dialog — an opt-out is not a
            // failure, and a CARRIER seeing "Failed" for their own driver would
            // reasonably re-enter the number and try again. Keyed on the
            // machine-readable reason, never on the message text.
            const optedOut = data.results.filter(r => r.reason === 'opted_out').map(r => r.recipient);
            const failed = data.results.filter(r => !r.ok && r.reason !== 'opted_out');
            const failedList = failed.map(r => r.recipient);
            const parts: string[] = [];
            if (data.sent > 0) parts.push(`Sent to ${data.sent} recipient(s).`);
            if (optedOut.length) parts.push(`${optedOut.join(', ')} opted out of texts — send by email instead.`);
            // Show the engine's reason per recipient, same as the scheduler
            // dialog (2026-08-17). A carrier mistyping their driver's number is
            // the likeliest failure on this screen, and "Failed" doesn't say so.
            if (failed.length) {
                parts.push(...failed.map(r => r.detail ? `${r.recipient}: ${r.detail}` : `Failed: ${r.recipient}.`));
            }
            if (data.note) {
                setMsg({ kind: 'warn', text: `${data.sent} email(s) sent. ${data.note}` });
            } else if (failedList.length || optedOut.length) {
                setMsg({ kind: 'warn', text: parts.join(' ') });
            } else {
                setMsg({ kind: 'ok', text: `Sent to ${data.sent} recipient(s).` });
            }
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Send failed.' });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Send directions</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {appt.FacilityName} — address, arrival instructions, and a map link for
                        appointment {appt.AppointmentNumber}.
                    </p>
                </div>

                <div className="px-6 py-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                        {(['email','sms','both'] as const).map(c => (
                            <button key={c} type="button" onClick={() => setChannel(c)}
                                className={`px-3 py-1 rounded-md border text-xs ${channel===c ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                {c==='email' ? 'Email' : c==='sms' ? 'Text' : 'Email + Text'}
                            </button>
                        ))}
                    </div>

                    {(channel==='email' || channel==='both') && (
                        <div>
                            <div className="flex gap-2">
                                <input type="email" value={emailInput} onChange={e=>setEmailInput(e.target.value)}
                                    onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addEmail(); } }}
                                    placeholder="driver@example.com"
                                    className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                <button type="button" onClick={addEmail} className="px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50">Add</button>
                            </div>
                            {emails.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {emails.map(em => (
                                        <span key={em} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs rounded px-2 py-0.5">
                                            {em}<button type="button" onClick={()=>setEmails(emails.filter(x=>x!==em))} className="text-blue-400 hover:text-blue-700">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {(channel==='sms' || channel==='both') && (
                        <div>
                            <div className="flex gap-2">
                                <input type="tel" value={phoneInput} onChange={e=>setPhoneInput(e.target.value)}
                                    onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addPhone(); } }}
                                    placeholder="+1 555 555 5555"
                                    className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                <button type="button" onClick={addPhone} className="px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50">Add</button>
                            </div>
                            {phones.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {phones.map(ph => (
                                        <span key={ph} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs rounded px-2 py-0.5">
                                            {ph}<button type="button" onClick={()=>setPhones(phones.filter(x=>x!==ph))} className="text-gray-400 hover:text-gray-700">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {msg && (
                        <div className={`text-xs rounded-md px-3 py-2 ${msg.kind==='ok' ? 'bg-green-50 text-green-700' : msg.kind==='warn' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                    <button onClick={onClose} disabled={sending} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">
                        Close
                    </button>
                    <button onClick={handleSend} disabled={sending}
                        className="text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-md px-4 py-1.5 disabled:opacity-50">
                        {sending ? 'Sending…' : 'Send directions'}
                    </button>
                </div>
            </div>
        </div>
    );
}

interface SendResponse {
    sent: number;
    anyFailed: boolean;
    note?: string;
    results: { channel: string; recipient: string; ok: boolean; detail?: string; reason?: 'opted_out' }[];
}

// ─── Driver modal ──────────────────────────────────────────────────────────
// Manage the LIST of drivers on this appointment (an appointment can have
// several — multi-truck pickup, or an LTL appointment booked with a longer
// window; see migration 134). Add an existing roster driver or a new one, and
// remove individual drivers. Each add/remove is its own immediate action.
// Backend: GET /carrier/drivers (own roster) +
// POST /carrier/appointments/{id}/drivers (add) +
// DELETE /carrier/appointments/{id}/drivers/{appointmentDriverId} (remove) --
// all scoped to the caller's own CarrierCode server-side.

interface CarrierDriverRow {
    driverId: number;
    name: string;
    phone: string;
    email: string | null;
    isActive: boolean;
}

function CarrierDriverModal({
    appt,
    onClose,
    onSaved,
}: {
    appt: Appointment;
    onClose: () => void;
    onSaved: () => Promise<void>;
}) {
    // Local working copy, updated in place so each add/remove shows immediately
    // without waiting for the parent refresh. It also re-syncs whenever the
    // parent hands down a newer version of this appointment, so the dialog can
    // never disagree with the list behind it.
    const [drivers, setDrivers] = useState(appt.drivers);
    useEffect(() => { setDrivers(appt.drivers); }, [appt.drivers]);
    const [mode, setMode] = useState<'pick' | 'new'>('pick');
    const [roster, setRoster] = useState<CarrierDriverRow[] | 'loading' | { error: string }>('loading');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [adding, setAdding] = useState(false);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await apiFetch<{ drivers: CarrierDriverRow[] }>('carrier/drivers');
                if (!cancelled) setRoster(data.drivers);
            } catch (e) {
                if (!cancelled) setRoster({ error: e instanceof Error ? e.message : 'Failed to load roster.' });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const resetAddForm = () => {
        setMode('pick'); setSelectedId(null); setName(''); setPhone(''); setEmail('');
    };

    const handleAdd = async () => {
        let body: object;
        if (mode === 'pick') {
            if (selectedId == null) { setMsg({ kind: 'err', text: 'Select a driver.' }); return; }
            body = { driverId: selectedId };
        } else {
            if (!name.trim() || !phone.trim()) { setMsg({ kind: 'err', text: 'Name and phone are required.' }); return; }
            body = { name: name.trim(), phone: phone.trim(), email: email.trim() || null };
        }
        setMsg(null);
        setAdding(true);
        try {
            const added = await apiFetch<{ appointmentDriverId: number; driverId: number | null; name: string; phone: string }>(
                `carrier/appointments/${appt.AppointmentId}/drivers`,
                { method: 'POST', body: JSON.stringify(body) },
            );
            setDrivers(prev => [...prev, added]);
            resetAddForm();
            setMsg({ kind: 'ok', text: `Added ${added.name}.` });
            await onSaved();
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Failed to add driver.' });
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (appointmentDriverId: number) => {
        setMsg(null);
        setRemovingId(appointmentDriverId);
        try {
            await apiFetch(
                `carrier/appointments/${appt.AppointmentId}/drivers/${appointmentDriverId}`,
                { method: 'DELETE' },
            );
            setDrivers(prev => prev.filter(d => d.appointmentDriverId !== appointmentDriverId));
            await onSaved();
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Failed to remove driver.' });
        } finally {
            setRemovingId(null);
        }
    };

    const activeRoster = Array.isArray(roster) ? roster.filter(d => d.isActive) : [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Drivers</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Appointment {appt.AppointmentNumber} — {appt.FacilityName}
                    </p>
                </div>

                <div className="px-6 py-4 space-y-3">
                    {/* Current drivers on this appointment */}
                    {drivers.length > 0 ? (
                        <div className="border border-gray-200 rounded-md divide-y">
                            {drivers.map(d => (
                                <div key={d.appointmentDriverId} className="flex items-center justify-between gap-2 px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">{d.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{d.phone}</div>
                                    </div>
                                    <button
                                        onClick={() => handleRemove(d.appointmentDriverId)}
                                        disabled={removingId === d.appointmentDriverId}
                                        className="text-xs text-red-500 hover:text-red-700 px-2 py-1 shrink-0 disabled:opacity-50"
                                    >
                                        {removingId === d.appointmentDriverId ? 'Removing…' : 'Remove'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-gray-400 border border-gray-200 rounded-md px-3 py-2">
                            No drivers on this appointment yet.
                        </div>
                    )}

                    <div className="text-xs font-medium text-gray-500 pt-1">Add a driver</div>

                    <div className="flex items-center gap-2 text-sm">
                        <button type="button" onClick={() => setMode('pick')}
                            className={`px-3 py-1 rounded-md border text-xs ${mode === 'pick' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            Pick from roster
                        </button>
                        <button type="button" onClick={() => setMode('new')}
                            className={`px-3 py-1 rounded-md border text-xs ${mode === 'new' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            Add new driver
                        </button>
                    </div>

                    {mode === 'pick' && (
                        <div>
                            {roster === 'loading' && <div className="text-xs text-gray-400">Loading roster…</div>}
                            {roster && !Array.isArray(roster) && roster !== 'loading' && (
                                <div className="text-xs text-red-600">Couldn&apos;t load roster — {roster.error}</div>
                            )}
                            {Array.isArray(roster) && activeRoster.length === 0 && (
                                <div className="text-xs text-gray-400">No drivers on file yet — add one instead.</div>
                            )}
                            {activeRoster.length > 0 && (
                                <select
                                    value={selectedId ?? ''}
                                    onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                >
                                    <option value="">Select a driver…</option>
                                    {activeRoster.map(d => (
                                        <option key={d.driverId} value={d.driverId}>{d.name} · {d.phone}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {mode === 'new' && (
                        <div className="space-y-2">
                            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Driver name"
                                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 555 5555"
                                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)"
                                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            <div className="text-[11px] text-gray-400">Saved to your roster for future appointments.</div>
                        </div>
                    )}

                    {msg && (
                        <div className={`text-xs rounded-md px-3 py-2 ${msg.kind === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                    <button onClick={onClose} disabled={adding} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">
                        Close
                    </button>
                    <button onClick={handleAdd} disabled={adding}
                        className="text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-md px-4 py-1.5 disabled:opacity-50">
                        {adding ? 'Adding…' : 'Add driver'}
                    </button>
                </div>
            </div>
        </div>
    );
}
